import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import anime from 'animejs';
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  Calendar,
  FileText,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  Bell,
  Search,
  Command,
  Mic,
  UserPlus,
  Plus,
  Zap,
  Briefcase,
  UserCircle,
  Flame,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
import { useGlobalModal } from '../../context/GlobalModalContext';
import { Button } from '../ui/Button';
import { Avatar } from '../ui/Avatar';

const navItems = [
  { path: '/whos-warm', label: "Who's Warm", icon: Flame, highlight: true },
  { path: '/dashboard', label: 'Mission Control', icon: LayoutDashboard, highlight: true },
  { path: '/accounts', label: 'Accounts', icon: Building2 },
  { path: '/contacts', label: 'Contacts', icon: UserCircle },
  { path: '/opportunities', label: 'Pipeline', icon: Briefcase },
  { path: '/context', label: 'Conversations', icon: MessageSquare },
  { path: '/meetings', label: 'Meetings', icon: Mic },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/sow', label: 'SOW', icon: FileText },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, organization, logout } = useAuth();
  const { openNewLeadModal } = useGlobalModal();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedActionIndex, setSelectedActionIndex] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const fabRef = useRef<HTMLButtonElement>(null);

  const currentPage = navItems.find((item) => item.path === location.pathname);

  // Quick actions for command palette
  const quickActions = [
    {
      id: 'new-lead',
      label: 'New Lead',
      description: 'Add a new lead to your pipeline',
      icon: UserPlus,
      shortcut: 'N',
      action: () => {
        setSearchOpen(false);
        openNewLeadModal();
      },
      highlight: true,
    },
    {
      id: 'pipeline',
      label: 'View Pipeline',
      description: 'See active opportunities',
      icon: Briefcase,
      action: () => {
        setSearchOpen(false);
        navigate('/opportunities');
      },
    },
    {
      id: 'accounts',
      label: 'View Accounts',
      description: 'See qualified accounts',
      icon: Building2,
      action: () => {
        setSearchOpen(false);
        navigate('/accounts');
      },
    },
    {
      id: 'hot-leads',
      label: 'View Hot Leads',
      description: 'See leads with high engagement',
      icon: Zap,
      action: () => {
        setSearchOpen(false);
        navigate('/whos-warm');
      },
    },
    {
      id: 'generate-sow',
      label: 'Generate SOW',
      description: 'Create a new statement of work',
      icon: FileText,
      action: () => {
        setSearchOpen(false);
        navigate('/sow');
      },
    },
    {
      id: 'analytics',
      label: 'View Analytics',
      description: 'See performance metrics',
      icon: BarChart3,
      action: () => {
        setSearchOpen(false);
        navigate('/analytics');
      },
    },
  ];

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setSelectedActionIndex(0);
      }

      // Close on escape
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }

      // Navigate command palette with arrows
      if (searchOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedActionIndex((prev) => (prev + 1) % quickActions.length);
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedActionIndex((prev) => (prev - 1 + quickActions.length) % quickActions.length);
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          quickActions[selectedActionIndex]?.action();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen, selectedActionIndex, quickActions]);

  // FAB entrance animation
  useEffect(() => {
    if (fabRef.current) {
      anime({
        targets: fabRef.current,
        scale: [0, 1],
        opacity: [0, 1],
        duration: 500,
        delay: 800,
        easing: 'easeOutExpo',
      });
    }
  }, []);

  // Navigation animation
  useEffect(() => {
    anime({
      targets: '.nav-item',
      translateX: [-20, 0],
      opacity: [0, 1],
      delay: anime.stagger(50, { start: 200 }),
      duration: 400,
      easing: 'easeOutQuad',
    });
  }, []);

  return (
    <div className="h-screen flex bg-slate-950 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 lg:translate-x-0 lg:static lg:z-auto',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="h-full flex flex-col bg-slate-950/98 backdrop-blur-xl border-r border-white/[0.06] relative overflow-hidden">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/[0.02] via-transparent to-transparent" />

          {/* Logo */}
          <div className="relative p-6 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <img src="/perihelio.svg" alt="" className="h-10 w-10" />
              <div>
                <h1 className="font-semibold text-white text-lg tracking-tight">Perihelio</h1>
                <p className="text-xs text-slate-500">
                  {organization?.name || 'AI-native sales intelligence'}
                </p>
              </div>
            </div>
            {/* Status indicator */}
            <div className="absolute top-5 right-5 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              <span className="text-[10px] text-slate-500">Online</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto scrollbar-none relative z-10">
            <p className="px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Navigation</p>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative',
                    'text-slate-400 hover:text-white text-sm',
                    isActive
                      ? 'bg-white/[0.06] text-white'
                      : 'hover:bg-white/[0.04]'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-500 rounded-r-full" />
                    )}
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center transition-all',
                      isActive
                        ? 'bg-primary-500/10'
                        : 'bg-transparent group-hover:bg-white/[0.04]'
                    )}>
                      <item.icon className={cn(
                        'h-[18px] w-[18px] transition-colors',
                        isActive ? 'text-primary-400' : 'text-slate-500 group-hover:text-slate-300'
                      )} />
                    </div>
                    <span className="flex-1 font-medium">{item.label}</span>
                    {item.highlight && (
                      <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-500/10">
                        <span className="w-1 h-1 bg-emerald-500 rounded-full" />
                        <span className="text-[10px] font-medium text-emerald-400">Live</span>
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* User */}
          <div className="p-3 border-t border-white/[0.06] relative z-10">
            <div className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.04] transition-colors cursor-pointer">
              <div className="relative">
                <Avatar name={user?.name || user?.email || 'User'} size="md" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-slate-950" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {user?.email}
                </p>
              </div>
              <Button variant="ghost" size="icon-sm" onClick={logout} className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-slate-950/90 backdrop-blur-xl">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-400 hover:text-white"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* Page title */}
            <div className="hidden lg:flex items-center gap-2">
              <h2 className="text-sm font-medium text-white">
                {currentPage?.label || 'Dashboard'}
              </h2>
              {currentPage?.highlight && (
                <span className="flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-400 rounded-md border border-emerald-500/20">
                  <span className="w-1 h-1 bg-emerald-500 rounded-full" />
                  Live
                </span>
              )}
            </div>

            {/* Search */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex-1 max-w-md mx-4 flex items-center gap-3 h-9 px-3 rounded-lg bg-white/[0.04] border border-white/[0.06] text-slate-500 hover:bg-white/[0.06] hover:border-white/[0.1] transition-all"
            >
              <Search className="h-4 w-4" />
              <span className="text-sm">Search...</span>
              <kbd className="ml-auto hidden sm:flex items-center gap-0.5 text-[10px] text-slate-600 bg-white/[0.06] px-1.5 py-0.5 rounded border border-white/[0.06]">
                <Command className="h-2.5 w-2.5" />K
              </kbd>
            </button>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* New Lead button - visible on desktop */}
              <Button
                onClick={() => openNewLeadModal()}
                size="sm"
                className="hidden md:inline-flex gap-1.5"
              >
                <UserPlus className="h-4 w-4" />
                <span>New Lead</span>
              </Button>

              <Button variant="ghost" size="icon-sm" className="relative text-slate-500 hover:text-white hover:bg-white/[0.06]">
                <Bell className="h-4 w-4" />
                <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 bg-red-500 rounded-full" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page content - scrollable inner area only */}
        <div className="flex-1 overflow-auto scrollbar-thin">
          {children}
        </div>
      </main>

      {/* Floating Action Button - Quick Add Lead */}
      <button
        ref={fabRef}
        onClick={() => openNewLeadModal()}
        className={cn(
          'fixed bottom-6 right-6 z-40',
          'w-14 h-14 rounded-2xl',
          'bg-gradient-to-br from-primary-500 to-primary-600',
          'border border-primary-400/30',
          'shadow-lg shadow-primary-500/30',
          'flex items-center justify-center',
          'hover:from-primary-400 hover:to-primary-500',
          'hover:shadow-xl hover:shadow-primary-500/40',
          'hover:scale-105',
          'active:scale-95',
          'transition-all duration-200',
          'group'
        )}
        style={{ opacity: 0, transform: 'scale(0)' }}
        aria-label="Add new lead"
      >
        <Plus className="w-6 h-6 text-white transition-transform group-hover:rotate-90 duration-300" />
        {/* Tooltip */}
        <span className="absolute right-full mr-3 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
          New Lead
          <kbd className="ml-2 text-[10px] text-slate-400 bg-slate-700 px-1.5 py-0.5 rounded">
            <Command className="w-2.5 h-2.5 inline mr-0.5" />N
          </kbd>
        </span>
      </button>

      {/* Command Palette / Search Modal */}
      {searchOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={() => setSearchOpen(false)}
          />
          <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg z-50 p-4">
            <div className="command-search rounded-2xl overflow-hidden">
              {/* Search input */}
              <div className="flex items-center gap-3 p-4 border-b border-white/[0.06]">
                <Search className="h-4 w-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search leads, companies, or actions..."
                  autoFocus
                  className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm"
                />
                <kbd className="text-[10px] text-slate-600 bg-white/[0.06] px-1.5 py-0.5 rounded border border-white/[0.06]">ESC</kbd>
              </div>

              {/* Quick Actions */}
              <div className="p-2">
                <p className="px-3 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">Quick Actions</p>
                {quickActions.map((action, index) => (
                  <button
                    key={action.id}
                    onClick={action.action}
                    onMouseEnter={() => setSelectedActionIndex(index)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150',
                      selectedActionIndex === index
                        ? 'bg-white/[0.08] text-white'
                        : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-300'
                    )}
                  >
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                      selectedActionIndex === index
                        ? action.highlight
                          ? 'bg-primary-500/20'
                          : 'bg-white/[0.06]'
                        : 'bg-white/[0.04]'
                    )}>
                      <action.icon className={cn(
                        'h-4 w-4',
                        selectedActionIndex === index && action.highlight
                          ? 'text-primary-400'
                          : selectedActionIndex === index
                          ? 'text-white'
                          : 'text-slate-500'
                      )} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={cn(
                        'text-sm font-medium',
                        selectedActionIndex === index ? 'text-white' : 'text-slate-300'
                      )}>
                        {action.label}
                      </p>
                      <p className="text-xs text-slate-500">{action.description}</p>
                    </div>
                    {action.shortcut && (
                      <kbd className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded border',
                        selectedActionIndex === index
                          ? 'text-slate-400 bg-white/[0.08] border-white/[0.1]'
                          : 'text-slate-600 bg-white/[0.04] border-white/[0.06]'
                      )}>
                        <Command className="w-2.5 h-2.5 inline mr-0.5" />{action.shortcut}
                      </kbd>
                    )}
                    {selectedActionIndex === index && (
                      <span className="text-[10px] text-slate-500">Enter</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Recent Leads */}
              <div className="p-2 border-t border-white/[0.06]">
                <p className="px-3 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">Recent Leads</p>
                <button className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-slate-400 hover:bg-white/[0.04] hover:text-slate-300 transition-colors">
                  <Avatar name="John Doe" size="sm" />
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-white">John Doe</p>
                    <p className="text-xs text-slate-500">VP Sales at Acme Corp</p>
                  </div>
                  <span className="text-sm font-semibold text-red-400">92</span>
                </button>
              </div>

              {/* Footer hints */}
              <div className="px-4 py-2.5 border-t border-white/[0.06] flex items-center justify-between text-[10px] text-slate-600">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">
                      <span className="text-[8px]">^</span>
                    </kbd>
                    <kbd className="px-1 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">
                      <span className="text-[8px]">v</span>
                    </kbd>
                    <span>Navigate</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">Enter</kbd>
                    <span>Select</span>
                  </span>
                </div>
                <span className="text-slate-700">Perihelio Command Palette</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
